import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Mail, Phone, Building2, Calendar, Hash, Search, Trash2, MessageSquare,
  Loader2, ExternalLink, Inbox
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  { value: 'contacted', label: 'Contacted', color: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  { value: 'quoted', label: 'Quoted', color: 'bg-purple-500/15 text-purple-300 border-purple-500/40' },
  { value: 'won', label: 'Won', color: 'bg-green-500/15 text-green-300 border-green-500/40' },
  { value: 'lost', label: 'Lost', color: 'bg-gray-500/15 text-gray-400 border-gray-500/40' },
];

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeLead, setActiveLead] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get('/leads');
      setLeads(res.data || []);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (leadId, status) => {
    try {
      await axios.patch(`/leads/${leadId}`, { status });
      setLeads(prev => prev.map(l => (l.id === leadId ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await axios.delete(`/leads/${leadId}`);
      setLeads(prev => prev.filter(l => l.id !== leadId));
      if (activeLead?.id === leadId) setActiveLead(null);
      toast.success('Lead deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filtered = leads.filter(l => {
    if (statusFilter !== 'all' && (l.status || 'new') !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return [l.name, l.email, l.phone, l.organization].some(v => v && v.toLowerCase().includes(s));
    }
    return true;
  });

  const counts = STATUS_OPTIONS.reduce((acc, opt) => {
    acc[opt.value] = leads.filter(l => (l.status || 'new') === opt.value).length;
    return acc;
  }, {});

  const statusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(o => o.value === (status || 'new')) || STATUS_OPTIONS[0];
    return (
      <span className={`text-xs px-2.5 py-1 rounded-full border ${opt.color} font-medium`}>
        {opt.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="leads-page">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Inbox className="w-7 h-7 text-purple-600" />Quote Requests
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Leads from the marketing site quote form
            </p>
          </div>
          <div className="text-sm text-gray-500">
            {leads.length} total · {counts.new || 0} new
          </div>
        </div>

        {/* Status filter pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
              statusFilter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200'
            }`} data-testid="filter-all">
            All ({leads.length})
          </button>
          {STATUS_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => setStatusFilter(opt.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                statusFilter === opt.value ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200'
              }`} data-testid={`filter-${opt.value}`}>
              {opt.label} ({counts[opt.value] || 0})
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-4 border-b">
            <div className="relative max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-9" placeholder="Search by name, email, phone..."
                value={search} onChange={e => setSearch(e.target.value)}
                data-testid="leads-search" />
            </div>
          </div>

          {loading ? (
            <div className="p-20 text-center"><Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-20 text-center text-gray-400">
              <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No quote requests yet</p>
              <p className="text-sm mt-1">When visitors fill out the marketing form, they'll appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Org</th>
                    <th className="px-4 py-3 text-left">Contact</th>
                    <th className="px-4 py-3 text-left">Qty / Deadline</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Received</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(lead => (
                    <tr key={lead.id} className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => setActiveLead(lead)}
                      data-testid={`lead-row-${lead.id}`}>
                      <td className="px-4 py-3 font-medium">{lead.name}</td>
                      <td className="px-4 py-3 text-gray-600">{lead.organization || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="text-xs">{lead.email}</div>
                        {lead.phone && <div className="text-xs text-gray-400">{lead.phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="text-xs">{lead.quantity ? `${lead.quantity} pcs` : '—'}</div>
                        {lead.deadline && <div className="text-xs text-gray-400">By {lead.deadline}</div>}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Select value={lead.status || 'new'} onValueChange={(v) => updateStatus(lead.id, v)}>
                          <SelectTrigger className="h-8 w-32 text-xs" data-testid={`status-${lead.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => deleteLead(lead.id)}
                          data-testid={`delete-${lead.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={!!activeLead} onOpenChange={(o) => !o && setActiveLead(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {activeLead?.name}
              {statusBadge(activeLead?.status)}
            </DialogTitle>
          </DialogHeader>
          {activeLead && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Email</div>
                  <a href={`mailto:${activeLead.email}`} className="flex items-center gap-1.5 text-purple-700 hover:underline">
                    <Mail className="w-3.5 h-3.5" />{activeLead.email}
                  </a>
                </div>
                {activeLead.phone && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Phone</div>
                    <a href={`tel:${activeLead.phone}`} className="flex items-center gap-1.5 text-purple-700 hover:underline">
                      <Phone className="w-3.5 h-3.5" />{activeLead.phone}
                    </a>
                  </div>
                )}
                {activeLead.organization && (
                  <div className="col-span-2">
                    <div className="text-xs text-gray-500 mb-1">Organization</div>
                    <div className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-gray-400" />{activeLead.organization}</div>
                  </div>
                )}
                {activeLead.quantity && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Quantity</div>
                    <div className="flex items-center gap-1.5"><Hash className="w-3.5 h-3.5 text-gray-400" />{activeLead.quantity}</div>
                  </div>
                )}
                {activeLead.deadline && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Deadline</div>
                    <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gray-400" />{activeLead.deadline}</div>
                  </div>
                )}
              </div>
              {activeLead.message && (
                <div>
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" />Message
                  </div>
                  <div className="bg-gray-50 rounded p-3 text-sm whitespace-pre-wrap border">{activeLead.message}</div>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t text-xs text-gray-500">
                <span>Received {new Date(activeLead.created_at).toLocaleString()}</span>
                <span>{activeLead.source}</span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" asChild>
                  <a href={`mailto:${activeLead.email}?subject=Re: Your quote request`} data-testid="reply-email-btn">
                    <Mail className="w-4 h-4 mr-2" />Reply via Email
                  </a>
                </Button>
                {activeLead.phone && (
                  <Button variant="outline" className="flex-1" asChild>
                    <a href={`tel:${activeLead.phone}`} data-testid="call-btn">
                      <Phone className="w-4 h-4 mr-2" />Call
                    </a>
                  </Button>
                )}
              </div>
              <Button className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={() => {
                  window.location.href = `/quote-builder?lead=${activeLead.id}&name=${encodeURIComponent(activeLead.name)}&email=${encodeURIComponent(activeLead.email)}&phone=${encodeURIComponent(activeLead.phone || '')}`;
                }}
                data-testid="convert-to-quote-btn">
                <ExternalLink className="w-4 h-4 mr-2" />Build a Proposal from this Lead
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
